import type { TdError, TdErrorKind } from "./generated";
import { ApiError, type ApiErrorCode } from "./taskApi";

/** Server error codes (`ErrorCode` in td-proto) that mean "try again later" rather than "your request is wrong". */
const BUSY_SERVER_CODES: ReadonlySet<string> = new Set(["Unavailable", "RateLimited"]);
const UNAUTHORIZED_SERVER_CODES: ReadonlySet<string> = new Set(["Unauthorized", "InvalidCredentials"]);

const UNAUTHORIZED_PATTERN = /^not authorized|invalid credentials|unauthorized/i;
const UNREACHABLE_PATTERN = /server unreachable|failed to fetch|networkerror|load failed/i;

/** For failures that carry only text, such as those the desktop shell reports as strings. */
export function codeForMessage(message: string): ApiErrorCode {
  if (UNAUTHORIZED_PATTERN.test(message)) return "unauthorized";
  if (UNREACHABLE_PATTERN.test(message)) return "unreachable";
  return "other";
}

export function codeForKind(kind: TdErrorKind, serverCode: string | undefined): ApiErrorCode {
  switch (kind) {
    case "unauthorized":
      return "unauthorized";
    case "unreachable":
      return "unreachable";
    case "server_busy":
      return "unavailable";
    case "rejected":
      if (serverCode !== undefined && UNAUTHORIZED_SERVER_CODES.has(serverCode)) return "unauthorized";
      if (serverCode !== undefined && BUSY_SERVER_CODES.has(serverCode)) return "unavailable";
      return "other";
    default:
      return "other";
  }
}

/** Normalizes whatever a boundary threw (a `TdError`, a structured object, an `Error`, a string) into an `ApiError`. */
export function toApiError(failure: unknown): ApiError {
  if (failure instanceof ApiError) return failure;
  if (isTdError(failure)) return new ApiError(codeForKind(failure.kind, failure.code), failure.message);
  if (isStructuredFailure(failure)) return new ApiError(failure.code, failure.message);
  const message = failure instanceof Error ? failure.message : String(failure);
  return new ApiError(codeForMessage(message), message);
}

const TD_ERROR_KINDS: ReadonlySet<string> = new Set<TdErrorKind>([
  "invalid_input",
  "command",
  "filter",
  "storage",
  "unreachable",
  "rejected",
  "invalid_response",
  "invalid_server_url",
  "protocol_mismatch",
  "unauthorized",
  "server_misbehaved",
  "nothing_to_reverse",
  "stale_undo_plan",
  "background_runtime_missing",
  "server_busy",
]);

function isTdError(value: unknown): value is TdError {
  if (!(value instanceof Error)) return false;
  const candidate = value as Error & Partial<Record<string, unknown>>;
  return typeof candidate["kind"] === "string" && TD_ERROR_KINDS.has(candidate["kind"]);
}

interface StructuredFailure {
  code: ApiErrorCode;
  message: string;
}

function isStructuredFailure(value: unknown): value is StructuredFailure {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<Record<string, unknown>>;
  return (
    typeof candidate["message"] === "string" &&
    (candidate["code"] === "unauthorized" ||
      candidate["code"] === "unreachable" ||
      candidate["code"] === "unavailable" ||
      candidate["code"] === "other")
  );
}
